import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Toolbar,
  Typography,
} from '@mui/material';
import { CloseCircle, EditParentIcon, NotVerifiedIcon, VerifiedIcon } from 'asset';
import { Layout, PopupCreateOrEditParent } from 'components';
import { DatePickerField } from 'components/form-control';
import { drawerWidth } from 'config';
import { hasPermission, NotifyService } from 'helpers';
import { useDateTime } from 'hooks';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { updateWalletCreditExpiryTestApi } from 'services';
import { useAppDispatch, useAppSelector } from 'store/hook';
import {
  initialAccountState,
  setParentDetailSuccess,
  setParentDetailTabState,
} from 'store/reducers/accounts';
import { GET_PARENT_DETAIL_REQUEST } from 'store/reducers/accounts/actionTypes';
import Activity from './Activity';
import Addresses from './Addresses';
import Chat from './Chat';
import Children from './Children';
import Documents from './Documents';
import Jobs from './Jobs';
import Notes from './Notes';
import PaymentHistory from './PaymentHistory';
import ServiceRequest from './ServiceRequest';
import Transactions from './Transactions';

const titleTab = [
  'Admin Notes',
  'Documents',
  'Service Request',
  'Jobs',
  'Top-ups History',
  'Transactions',
  'Activity',
  'Chat',
  'Addresses',
  'Children',
];
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const ParentDetail = () => {
  const dispatch = useAppDispatch();
  const { formatDateTime, moment } = useDateTime();
  const { parent_id } = useParams();
  const [tabValue, setTabValue] = useState(0);
  const [openEditPopup, setOpenEditPopup] = useState(false);
  const [openEditWalletExpiryPopup, setOpenEditWalletExpiryPopup] =
    useState(false);
  const [savingWalletExpiry, setSavingWalletExpiry] = useState(false);
  const [aaCreditExpiredAt, setAaCreditExpiredAt] = useState<any>(null);
  const [acCreditExpiredAt, setAcCreditExpiredAt] = useState<any>(null);
  const { parent_detail } = useAppSelector(
    (state) => state.accountReducer.parents
  );
  const navigate = useNavigate();

  const setOpenEdit = (event: any) => {
    event.stopPropagation();
    setOpenEditPopup(true);
  };
  const onClosePopup = (txt: string) => {
    if (txt === 'edit') {
      setOpenEditPopup(false);
    }
  };

  const openWalletExpiryPopup = () => {
    setAaCreditExpiredAt(
      parent_detail?.aa_credit_expired_at
        ? moment(parent_detail.aa_credit_expired_at)
        : null
    );
    setAcCreditExpiredAt(
      parent_detail?.ac_credit_expired_at
        ? moment(parent_detail.ac_credit_expired_at)
        : null
    );
    setOpenEditWalletExpiryPopup(true);
  };

  const closeWalletExpiryPopup = () => {
    setOpenEditWalletExpiryPopup(false);
    setAaCreditExpiredAt(null);
    setAcCreditExpiredAt(null);
  };

  const submitWalletExpiry = async () => {
    if (!parent_detail?.phone_number) {
      NotifyService.error('Parent phone number is required');
      return;
    }

    try {
      setSavingWalletExpiry(true);
      await updateWalletCreditExpiryTestApi({
        phone_number: parent_detail.phone_number,
        aa_credit_expired_at: aaCreditExpiredAt
          ? moment(aaCreditExpiredAt).endOf('day').toISOString()
          : null,
        ac_credit_expired_at: acCreditExpiredAt
          ? moment(acCreditExpiredAt).endOf('day').toISOString()
          : null,
      });
      dispatch({
        type: GET_PARENT_DETAIL_REQUEST,
        payload: { id: parent_id },
      });
      closeWalletExpiryPopup();
      NotifyService.success('Wallet credit expiry updated successfully');
    } catch (error) {
      NotifyService.error(error);
    } finally {
      setSavingWalletExpiry(false);
    }
  };

  const handleChangeTabValue = (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setTabValue(newValue);
  };

  const onSuccessDeleteParent = () => {
    dispatch({
      type: GET_PARENT_DETAIL_REQUEST,
      payload: { id: parent_id },
    });
  };

  useEffect(() => {
    dispatch({
      type: GET_PARENT_DETAIL_REQUEST,
      payload: { id: parent_id },
    });
    return () => {
      dispatch(setParentDetailSuccess(null));
      dispatch(
        setParentDetailTabState(initialAccountState.parents.parent_detail_tab)
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parent_id]);

  return (
    <Layout>
      <AppBar
        position="fixed"
        sx={{
          width: `calc(100% - ${drawerWidth}px)`,
          ml: `${drawerWidth}px`,
          backgroundColor: 'white.light',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        }}
        elevation={0}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Grid>
            <Typography
              sx={{
                fontSize: '20px',
                lineHeight: 1,
                color: 'grey1.light',
              }}
            >
              Accounts / Parents /{' '}
              {parent_detail?.first_name && parent_detail?.last_name
                ? parent_detail?.first_name + ' ' + parent_detail?.last_name
                : 'No data to display yet'}
            </Typography>
          </Grid>
        </Toolbar>
      </AppBar>
      <Paper
        elevation={0}
        sx={{
          p: '16px',
        }}
      >
        <Grid container justifyContent="space-between" pb="16px">
          <Button
            startIcon={<ArrowBackIosNewIcon />}
            sx={{
              p: 0,
              color: 'black.light',
              ':hover': {
                backgroundColor: 'white.light',
              },
            }}
            variant="text"
            onClick={() => {
              navigate(`/accounts/parents`);
            }}
          >
            Back
          </Button>
          {hasPermission(['superadmin', 'staff']) && (
            <Stack direction="row" spacing={2}>
              <Button
                sx={{
                  p: 0,
                }}
                onClick={openWalletExpiryPopup}
              >
                Edit Wallet Expiry
              </Button>
              <Button
                startIcon={<EditParentIcon />}
                sx={{
                  p: 0,
                }}
                onClick={(e) => setOpenEdit(e)}
              >
                Edit Parent
              </Button>
            </Stack>
          )}
        </Grid>
        <Stack
          spacing="55px"
          direction="row"
          alignItems="center"
          sx={{
            backgroundColor: 'rgba(34, 111, 227, 0.04)',
            p: '21px 31px',
          }}
        >
          <Stack direction="row" spacing="16px" alignItems="center">
            <Avatar
              src={
                parent_detail?.profile_picture
                  ? parent_detail?.profile_picture
                  : ''
              }
              sx={{
                width: '40px',
                height: '40px',
                marginRight: '6px',
              }}
            />
            <Grid display="flex" flexDirection="column" gap="4px">
              <Typography
                fontWeight={500}
                lineHeight="14px"
                fontSize={14}
                letterSpacing="0.1px"
                color="text.secondary"
              >
                Parent Name (First+Last)
              </Typography>
              <Typography
                fontWeight={400}
                lineHeight="16px"
                fontSize={14}
                letterSpacing="0.25px"
                color="black.light"
              >
                {parent_detail?.first_name && parent_detail?.last_name
                  ? parent_detail?.first_name + ' ' + parent_detail?.last_name
                  : 'No data to display yet'}
              </Typography>
            </Grid>
          </Stack>
          <Grid display="flex" flexDirection="column" gap="4px">
            <Typography
              fontWeight={500}
              lineHeight="14px"
              fontSize={14}
              letterSpacing="0.1px"
              color="text.secondary"
            >
              Email
            </Typography>
            <Grid container>
              <Typography
                fontWeight={400}
                lineHeight="16px"
                fontSize={14}
                letterSpacing="0.25px"
                color="black.light"
                display="flex"
              >
                {parent_detail?.email
                  ? parent_detail?.email
                  : 'No data to display yet'}
              </Typography>
              <Grid display="flex" alignItems="center" ml="16px">
                {parent_detail?.email_verified ? (
                  <>
                    <Typography
                      component="span"
                      variant="Caption_12_Medium"
                      display="flex"
                      alignItems="center"
                      color="#AEAEB2"
                    >
                      Verified &nbsp;
                    </Typography>
                    <VerifiedIcon />
                  </>
                ) : (
                  <>
                    <Typography
                      component="span"
                      variant="Caption_12_Medium"
                      display="flex"
                      alignItems="center"
                      color="#AEAEB2"
                    >
                      Not verified &nbsp;
                    </Typography>
                    <NotVerifiedIcon />
                  </>
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid display="flex" flexDirection="column" gap="4px" ml="16px">
            <Typography
              fontWeight={500}
              lineHeight="14px"
              fontSize={14}
              letterSpacing="0.1px"
              color="text.secondary"
            >
              Phone number
            </Typography>
            <Grid container>
              <Typography variant="Components_UnstyledContainer">
                {parent_detail?.phone_number
                  ? parent_detail?.phone_number
                  : 'No data to display yet'}
              </Typography>
              <Grid display="flex" alignItems="center" ml="16px">
                {parent_detail?.phone_number_verified ? (
                  <>
                    <Typography
                      component="span"
                      variant="Caption_12_Medium"
                      display="flex"
                      alignItems="center"
                      color="#AEAEB2"
                    >
                      Verified &nbsp;
                    </Typography>
                    <VerifiedIcon />
                  </>
                ) : (
                  <>
                    <Typography
                      component="span"
                      variant="Caption_12_Medium"
                      display="flex"
                      alignItems="center"
                      color="#AEAEB2"
                    >
                      Not verified &nbsp;
                    </Typography>
                    <NotVerifiedIcon />
                  </>
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid display="flex" flexDirection="column" gap="4px" ml="16px">
            <Typography
              fontWeight={500}
              lineHeight="14px"
              fontSize={14}
              letterSpacing="0.1px"
              color="text.secondary"
            >
              Aunty Credits
            </Typography>
            <Grid container>
              <Typography variant="Components_UnstyledContainer">
                {parent_detail?.aunty_credits_balance ||
                parent_detail?.aunty_credits_balance === 0
                  ? `$${Number(parent_detail?.aunty_credits_balance).toFixed(
                      2
                    )}`
                  : 'No data to display yet'}
              </Typography>
            </Grid>
            <Typography
              fontWeight={400}
              lineHeight="14px"
              fontSize={12}
              letterSpacing="0.1px"
              color="text.secondary"
            >
              Expiry:{' '}
              {parent_detail?.aa_credit_expired_at
                ? formatDateTime(
                    parent_detail.aa_credit_expired_at,
                    'DD MMM YYYY'
                  )
                : '-'}
            </Typography>
          </Grid>
          <Grid display="flex" flexDirection="column" gap="4px" ml="16px">
            <Typography
              fontWeight={500}
              lineHeight="14px"
              fontSize={14}
              letterSpacing="0.1px"
              color="text.secondary"
            >
              Aunty Concierge Credits
            </Typography>
            <Grid container>
              <Typography variant="Components_UnstyledContainer">
                {parent_detail?.ac_balance || parent_detail?.ac_balance === 0
                  ? `$${Number(parent_detail?.ac_balance).toFixed(2)}`
                  : 'No data to display yet'}
              </Typography>
            </Grid>
            <Typography
              fontWeight={400}
              lineHeight="14px"
              fontSize={12}
              letterSpacing="0.1px"
              color="text.secondary"
            >
              Expiry:{' '}
              {parent_detail?.ac_credit_expired_at
                ? formatDateTime(
                    parent_detail.ac_credit_expired_at,
                    'DD MMM YYYY'
                  )
                : '-'}
            </Typography>
          </Grid>
        </Stack>
        <Grid
          container
          sx={{
            borderBottom: '1px solid  rgba(0, 0, 0, 0.08)',
            marginBottom: '16px',
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleChangeTabValue}
            aria-label="basic tabs example"
            variant="scrollable"
            scrollButtons="auto"
          >
            {titleTab.map((item, index) => {
              return (
                <Tab
                  key={index}
                  label={item}
                  {...a11yProps(index)}
                  sx={{
                    fontSize: '16px',
                    fontWeight: 400,
                    lineHeight: '125%',
                    letterSpacing: '0.1px',
                    color: '#959595',
                    textTransform: 'capitalize',
                  }}
                />
              );
            })}
          </Tabs>
        </Grid>
        <TabPanel value={tabValue} index={0}>
          <Notes parentId={parent_id} />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <Documents parent_id={parent_id} />
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <ServiceRequest parentId={parent_id} />
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          <Jobs parentId={parent_id} />
        </TabPanel>
        <TabPanel value={tabValue} index={4}>
          <PaymentHistory parentId={parent_id} />
        </TabPanel>
        <TabPanel value={tabValue} index={5}>
          <Transactions parentId={parent_id} />
        </TabPanel>
        <TabPanel value={tabValue} index={6}>
          <Activity parentId={parent_id} />
        </TabPanel>
        <TabPanel value={tabValue} index={7}>
          <Chat parentId={parent_id} />
        </TabPanel>
        <TabPanel value={tabValue} index={8}>
          <Addresses parentId={parent_id} />
        </TabPanel>
        <TabPanel value={tabValue} index={9}>
          <Children parentId={parent_id} />
        </TabPanel>
      </Paper>
      <PopupCreateOrEditParent
        open={openEditPopup}
        handleClosePopup={() => onClosePopup('edit')}
        subject={parent_id}
        onSuccess={onSuccessDeleteParent}
      />
      <Dialog
        open={openEditWalletExpiryPopup}
        sx={{
          '& .MuiPaper-root': {
            width: 500,
            borderRadius: 0,
          },
        }}
      >
        <Grid>
          <Grid
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px 25px',
            }}
          >
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '16px',
                lineHeight: '100%',
              }}
            >
              Edit Wallet Credit Expiry
            </Typography>
            <IconButton onClick={closeWalletExpiryPopup} sx={{ padding: 0 }}>
              <CloseCircle />
            </IconButton>
          </Grid>
          <Divider sx={{ borderColor: '#DEE2E6' }} />
          <Stack height="100%" justifyContent="space-between">
            <Stack spacing="16px" p="25px">
              <Grid>
                <DatePickerField
                  title="AA Credit Expiry"
                  name="aa_credit_expired_at"
                  value={aaCreditExpiredAt}
                  onChange={setAaCreditExpiredAt}
                  isClearable
                />
              </Grid>
              <Grid>
                <DatePickerField
                  title="AC Credit Expiry"
                  name="ac_credit_expired_at"
                  value={acCreditExpiredAt}
                  onChange={setAcCreditExpiredAt}
                  isClearable
                />
              </Grid>
            </Stack>
            <Stack
              direction="row"
              justifyContent="space-between"
              spacing="8px"
              p="0px 25px 25px"
            >
              <Button
                variant="outlined"
                color="secondary"
                sx={{
                  width: '233.5px',
                  height: '30px',
                }}
                onClick={closeWalletExpiryPopup}
              >
                Cancel
              </Button>
              <Button
                color="secondary"
                variant="contained"
                sx={{
                  width: '233.5px',
                  height: '30px',
                }}
                type="button"
                disabled={savingWalletExpiry}
                onClick={submitWalletExpiry}
              >
                Update
              </Button>
            </Stack>
          </Stack>
        </Grid>
      </Dialog>
    </Layout>
  );
};

export default ParentDetail;
